import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Divider, Drawer, Flex, Modal, Typography } from 'antd';
import cn from 'classnames';
import dayjs from 'dayjs';

import { useDesktopMediaQuery } from '@/shared/lib/hooks';
import { ConfigurableTable } from '@/shared/ui/table';

import { parseNegativeNumber } from '../../lib/parse-negative-number';
import { useGetTradingPlatformSessionCurrency } from '../../models/selectors/get-trading-platform-session-currency';
import type { CalendarData } from '../../models/types/transformed-calendar-data';
import { TradingPlatformDailyInfoChart } from '../trading-platform-daily-info-chart/trading-platform-daily-info-chart';
import { TradingPlatformDailyInfoNotes } from '../trading-platform-daily-info-notes/trading-platform-daily-info-notes';
import { TradingPlatformDailyInfoTitle } from '../trading-platform-daily-info-title/trading-platform-daily-info-title';

import cls from './trading-platform-daily-info.module.css';

interface TradingPlatformDailyInfoProps {
    isOpened: boolean;
    onClose: VoidFunction;
    data: CalendarData | undefined;
}

export const TradingPlatformDailyInfo = memo(
    (props: TradingPlatformDailyInfoProps) => {
        const { isOpened, onClose, data } = props;

        const { t } = useTranslation('trading-journal');

        const isDesktop = useDesktopMediaQuery();

        const currency = useGetTradingPlatformSessionCurrency();

        const { winningTrades, losingTrades, totalCommission, totalVolume } =
            useMemo(() => {
                if (!data?.trades) {
                    return {
                        losingTrades: 0,
                        totalCommission: 0,
                        totalVolume: 0,
                        winningTrades: 0,
                    };
                }

                const winning = data.trades.filter(
                    (trade) => trade.net_profit > 0,
                ).length;
                const losing = data.trades.filter(
                    (trade) => trade.net_profit <= 0,
                ).length;

                const commission = data.trades.reduce(
                    (sum, trade) =>
                        sum +
                        (trade.commission !== undefined &&
                        trade.commission !== null
                            ? trade.commission
                            : 0),
                    0,
                );

                const volume = data.trades.reduce(
                    (sum, trade) => sum + trade.volume,
                    0,
                );

                return {
                    losingTrades: losing,
                    totalCommission: commission,
                    totalVolume: volume,
                    winningTrades: winning,
                };
            }, [data]);

        const extendedColumns = useMemo(
            () => ({
                open_time: {
                    title: t('tradingJournalDailyInfoTableColumnOpenTime'),
                    render: (value: number) =>
                        dayjs(value).format('YYYY-MM-DD HH:mm:ss'),
                },
                close_time: {
                    title: t('tradingJournalDailyInfoTableColumnCloseTime'),
                    render: (value: number) =>
                        dayjs(value).format('YYYY-MM-DD HH:mm:ss'),
                },
                symbol: {
                    title: t('tradingJournalDailyInfoTableColumnSymbol'),
                },
                type: {
                    title: t('tradingJournalDailyInfoTableColumnType'),
                    render: (value: 'sell' | 'buy') => (
                        <span
                            className={
                                value === 'sell'
                                    ? cls.tradeTypeSell
                                    : cls.tradeTypeBuy
                            }
                        >
                            {t(
                                value === 'sell'
                                    ? 'tradingJournalDailyInfoTradeOperationSell'
                                    : 'tradingJournalDailyInfoTradeOperationBuy',
                            )}
                        </span>
                    ),
                },
                volume: {
                    title: t('tradingJournalDailyInfoTableColumnVolume'),
                },
                net_profit: {
                    title: t('tradingJournalDailyInfoTableColumnNetProfit'),
                    render: (value: number) => (
                        <Typography.Text
                            className={value >= 0 ? cls.up : cls.down}
                        >
                            {parseNegativeNumber(value, currency)}
                        </Typography.Text>
                    ),
                },
                net_roi: {
                    title: t('tradingJournalDailyInfoTableColumnNetRoi'),
                    render: (value: number) => `${value}%`,
                },
            }),
            [currency, t],
        );

        const getRowClassName = useCallback(
            (record: { net_profit: number }) => {
                if (record.net_profit > 0) {
                    return cls.winningRow;
                }

                if (record.net_profit < 0) {
                    return cls.losingRow;
                }

                return '';
            },
            [],
        );

        if (!data) {
            return null;
        }

        const title = (
            <Flex
                align="center"
                justify="space-between"
            >
                <TradingPlatformDailyInfoTitle
                    dailyNetPL={data.total_profit}
                    date={data.date}
                />
                <Button
                    className={cls.closeButton}
                    onClick={onClose}
                    size="small"
                    type="link"
                >
                    {t('tradingJournalDailyInfoClose')}
                </Button>
            </Flex>
        );

        const content = (
            <>
                <Flex
                    className={cls.dataSection}
                    gap={isDesktop ? 40 : 24}
                >
                    <TradingPlatformDailyInfoChart data={data.chartData} />

                    <Flex className={cls.dataContainer}>
                        <Flex
                            className={cls.left}
                            gap={16}
                            vertical
                        >
                            <Flex
                                align="center"
                                justify="space-between"
                            >
                                <Typography.Text className={cls.title}>
                                    {t(
                                        'tradingJournalDailyInfoDataTotalProfit',
                                    )}
                                </Typography.Text>
                                <Typography.Text className={cls.value}>
                                    {parseNegativeNumber(
                                        data.total_profit,
                                        currency,
                                    )}
                                </Typography.Text>
                            </Flex>

                            <Flex
                                align="center"
                                justify="space-between"
                            >
                                <Typography.Text className={cls.title}>
                                    {t('tradingJournalDailyInfoDataWinRate')}
                                </Typography.Text>
                                <Typography.Text className={cls.value}>
                                    {data.total_win_percent}%
                                </Typography.Text>
                            </Flex>

                            <Flex
                                align="center"
                                justify="space-between"
                            >
                                <Typography.Text className={cls.title}>
                                    {t(
                                        'tradingJournalDailyInfoDataProfitFactor',
                                    )}
                                </Typography.Text>
                                <Typography.Text className={cls.value}>
                                    {!data.profit_factor
                                        ? '--'
                                        : data.profit_factor.toFixed(2)}
                                </Typography.Text>
                            </Flex>

                            <Flex
                                align="center"
                                justify="space-between"
                            >
                                <Typography.Text className={cls.title}>
                                    {t(
                                        'tradingJournalDailyInfoDataSumCommission',
                                    )}
                                </Typography.Text>
                                <Typography.Text className={cls.value}>
                                    {totalCommission === 0
                                        ? '--'
                                        : parseNegativeNumber(
                                              totalCommission,
                                              currency,
                                          )}
                                </Typography.Text>
                            </Flex>

                            <Flex
                                align="center"
                                justify="space-between"
                            >
                                <Typography.Text className={cls.title}>
                                    {t(
                                        'tradingJournalDailyInfoDataSumVolumeTotal',
                                    )}
                                </Typography.Text>
                                <Typography.Text className={cls.value}>
                                    {totalVolume === 0
                                        ? '--'
                                        : totalVolume.toFixed(2)}
                                </Typography.Text>
                            </Flex>
                        </Flex>

                        <Divider
                            className={cls.divider}
                            type="vertical"
                        />

                        <Flex
                            className={cls.right}
                            gap={16}
                            vertical
                        >
                            <Flex
                                align="center"
                                justify="space-between"
                            >
                                <Typography.Text className={cls.title}>
                                    {t('tradingJournalDailyInfoDataTradeCount')}
                                </Typography.Text>
                                <Typography.Text className={cls.value}>
                                    {data.trades.length}
                                </Typography.Text>
                            </Flex>

                            <Flex
                                align="center"
                                justify="space-between"
                            >
                                <Typography.Text className={cls.title}>
                                    {t(
                                        'tradingJournalDailyInfoDataWinTradeCount',
                                    )}
                                </Typography.Text>
                                <Typography.Text className={cls.value}>
                                    {winningTrades}
                                </Typography.Text>
                            </Flex>

                            <Flex
                                align="center"
                                justify="space-between"
                            >
                                <Typography.Text className={cls.title}>
                                    {t(
                                        'tradingJournalDailyInfoDataLossTradeCount',
                                    )}
                                </Typography.Text>
                                <Typography.Text className={cls.value}>
                                    {losingTrades}
                                </Typography.Text>
                            </Flex>

                            <Flex
                                align="center"
                                justify="space-between"
                            >
                                <Typography.Text className={cls.title}>
                                    {t(
                                        'tradingJournalDailyInfoDataAverageWinTrade',
                                    )}
                                </Typography.Text>
                                <Typography.Text className={cls.value}>
                                    {parseNegativeNumber(
                                        data.average_win_trade,
                                        currency,
                                    )}
                                </Typography.Text>
                            </Flex>

                            <Flex
                                align="center"
                                justify="space-between"
                            >
                                <Typography.Text className={cls.title}>
                                    {t(
                                        'tradingJournalDailyInfoDataAverageLossTrade',
                                    )}
                                </Typography.Text>
                                <Typography.Text className={cls.value}>
                                    {parseNegativeNumber(
                                        data.average_loss_trade,
                                        currency,
                                    )}
                                </Typography.Text>
                            </Flex>
                        </Flex>
                    </Flex>
                </Flex>

                <ConfigurableTable
                    bordered
                    className={cls.table}
                    dataSource={data.trades}
                    extendedColumns={extendedColumns}
                    pagination={false}
                    rowClassName={getRowClassName}
                    rowKey="open_time"
                    scroll={{ y: 175 }}
                />

                <div className={cls.noteContainer}>
                    <TradingPlatformDailyInfoNotes date={data.date} />
                </div>
            </>
        );

        const isPositiveDay = data.total_profit >= 0;
        const rootClassName = cn(cls.root, {
            [cls.positiveDay]: isPositiveDay,
            [cls.negativeDay]: !isPositiveDay,
        });

        return isDesktop ? (
            <Modal
                centered
                className={rootClassName}
                closable={false}
                destroyOnClose
                footer={null}
                onCancel={onClose}
                open={isOpened}
                title={title}
                width={960}
            >
                {content}
            </Modal>
        ) : (
            <Drawer
                className={rootClassName}
                closeIcon={null}
                destroyOnClose
                height="80vh"
                onClose={onClose}
                open={isOpened}
                placement="bottom"
                title={title}
            >
                {content}
            </Drawer>
        );
    },
);

TradingPlatformDailyInfo.displayName = 'TradingPlatformDailyInfo';
